import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Banknote, Plus, ArrowUpRight, ArrowDownRight, History } from "lucide-react";
import { toast } from "sonner";
import { KPICard, DataTable, DepartmentBadge } from "@/components/UIComponents";
import { formatCurrency, CONFIG } from "@/lib/config";
import api from "@/lib/api";

export const ArgentCash = () => {
  const [loading, setLoading] = useState(true);
  const [solde, setSolde] = useState(0);
  const [historique, setHistorique] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type_operation: "depot",
    montant: "",
    description: "",
    departement: "PRQC",
    date: new Date().toISOString().split('T')[0]
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [soldeData, hist] = await Promise.all([
        api.getCashSolde(),
        api.getCashHistorique(50)
      ]);
      setSolde(soldeData.solde);
      setHistorique(hist);
    } catch (error) {
      console.error("Error:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.montant || parseFloat(formData.montant) <= 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }

    try {
      await api.createCashEntry({
        ...formData,
        montant: parseFloat(formData.montant)
      });
      toast.success(`${formData.type_operation === 'depot' ? 'Dépôt' : 'Retrait'} enregistré!`);
      setFormData({
        type_operation: "depot",
        montant: "",
        description: "",
        departement: "PRQC",
        date: new Date().toISOString().split('T')[0]
      });
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const columns = [
    { key: "date", header: "Date", className: "money-value text-sm text-slate-600" },
    { 
      key: "type_operation", 
      header: "Type",
      render: (val) => (
        <span className={`flex items-center gap-1 text-sm ${val === 'depot' ? 'text-emerald-600' : 'text-red-500'}`}>
          {val === 'depot' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          {val === 'depot' ? 'Dépôt' : 'Retrait'}
        </span>
      )
    },
    { key: "description", header: "Description", className: "text-sm text-slate-900" },
    { 
      key: "departement", 
      header: "Département",
      render: (val) => <DepartmentBadge dept={val} />
    },
    { 
      key: "montant", 
      header: "Montant", 
      align: "right",
      render: (val, row) => (
        <span className={`money-value font-medium ${row.type_operation === 'depot' ? 'text-emerald-600' : 'text-red-500'}`}>
          {row.type_operation === 'depot' ? '+' : '-'}{formatCurrency(val)}
        </span>
      )
    },
    { 
      key: "solde_apres", 
      header: "Solde après", 
      align: "right",
      className: "money-value font-medium text-slate-900"
,
      render: (val) => formatCurrency(val)
    }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  // Calculs
  const totalDepots = historique.filter(h => h.type_operation === 'depot').reduce((sum, h) => sum + h.montant, 0);
  const totalRetraits = historique.filter(h => h.type_operation === 'retrait').reduce((sum, h) => sum + h.montant, 0);

  return (
    <div data-testid="cash-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Argent Cash</h1>
          <p className="text-slate-500 mt-1 text-sm">Gestion de la caisse physique</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bg-emerald-900 hover:bg-emerald-800 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle opération
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Solde Actuel"
          value={formatCurrency(solde)}
          subtitle="Caisse physique"
          icon={Banknote}
          color="purple"
        />
        <KPICard
          title="Total Dépôts"
          value={formatCurrency(totalDepots)}
          subtitle="Historique complet"
          icon={ArrowUpRight}
          color="emerald"
        />
        <KPICard
          title="Total Retraits"
          value={formatCurrency(totalRetraits)}
          subtitle="Historique complet"
          icon={ArrowDownRight}
          color="red"
        />
      </div>

      {/* Formulaire */}
      {showForm && (
        <Card className="animate-fade-in border-2 border-emerald-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Nouvelle Opération Cash</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={formData.type_operation} onValueChange={(v) => setFormData({...formData, type_operation: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="depot">Dépôt</SelectItem>
                    <SelectItem value="retrait">Retrait</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Montant ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) => setFormData({...formData, montant: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Département</Label>
                <Select value={formData.departement} onValueChange={(v) => setFormData({...formData, departement: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CONFIG.departments.map(d => (
                      <SelectItem key={d} value={d}>{CONFIG.department_names[d]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="lg:col-span-2">
                <Label>Description</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Description de l'opération"
                />
              </div>
              <div className="lg:col-span-6 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800">
                  Enregistrer
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Historique */}
      <Card className="animate-fade-in delay-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Historique des Opérations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={historique} />
        </CardContent>
      </Card>
    </div>
  );
};

export default ArgentCash;
